/**
 * TenantRoute: wraps a route element that is only meaningful inside a tenant.
 * At the platform apex it redirects to the hosted-ecosystem listing instead of
 * letting the page render against a null tenant.
 *
 * Why this exists separately from the other guards: none of them cover this
 * case. `CapabilityRoute` is deliberately a no-op at the apex, because
 * capabilities are a per-tenant concept and there is no tenant to read them
 * from. `ProtectedRoutes` has a tenant-scoped prefix list, but only guards
 * routes it wraps, and these pages are public. So a tenant-only public page
 * reached at the apex passed through every existing gate and rendered.
 *
 * What that looked like: `/directory` at the apex rendered the full directory
 * chrome and reported "0 organizations", while the same page under a tenant
 * reported 323. The queries were correct, since no tenant means no scope means
 * no rows. The page just had no way to say "you are not in an ecosystem", so
 * it said the ecosystem was empty instead.
 *
 * Redirect target is `/ecosystems`, the hosted-ecosystem listing, because it
 * answers the question the user actually has. `/` would drop them somewhere
 * with no relationship to what they clicked, and `/choose-ecosystem` requires
 * a signed-in user while these pages are public.
 *
 * A FAILED resolution is not the same as no tenant, and must not redirect.
 * EcosystemContext falls open to platform mode when the by-slug lookup errors
 * or returns an unexpected status, so a transient network blip on
 * `/costem/directory` presents as `isPlatform`. Redirecting there would send a
 * real tenant user away from their own URL and, because the redirect replaces
 * history, leave the back button unable to recover it. A clean 404 (the URL is
 * genuinely not a tenant) sets no error, which is what separates the two.
 *
 * Compose OUTSIDE the capability gate, so a tenant-less request is answered
 * before asking a question that has no meaning without a tenant:
 *
 *   <TenantRoute>
 *       <CapabilityRoute name="mentoring">
 *           <PublicRoutes>
 *               <MentorsPage />
 *           </PublicRoutes>
 *       </CapabilityRoute>
 *   </TenantRoute>
 *
 * Like the sibling guards this is a UX affordance, not a security boundary.
 * The server already returns nothing for a tenant-less request; this stops the
 * client presenting that emptiness as a fact about an ecosystem.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';

export default function TenantRoute({ children }) {
    const { isPlatform, loading, error } = useEcosystem();

    // Defensive only: EcosystemProvider withholds children until resolution
    // finishes, so this guard does not mount while loading. Kept for symmetry
    // with the other route guards and so the component is correct on its own
    // terms rather than only in its current parent.
    if (loading) {
        return null;
    }

    // Resolution failed rather than resolving to no tenant. Render the page and
    // let it show its own error/empty state, which a reload can recover from.
    if (error) {
        return children;
    }

    if (isPlatform) {
        return <Navigate to="/ecosystems" replace />;
    }

    return children;
}

TenantRoute.propTypes = {
    children: PropTypes.node.isRequired,
};
